import { useState, useEffect } from 'react'
import { useText, usePublishText } from '../hooks/useText'
import { Button } from '../components/ui/Button'
import { Alert } from '../components/ui/Alert'
import { Spinner } from '../components/ui/Spinner'
import { Save, RotateCcw } from 'lucide-react'
import { CollapsibleSection } from '../components/TextEditor/CollapsibleSection'
import { TextInput } from '../components/TextEditor/TextInput'
import { LinkInput } from '../components/TextEditor/LinkInput'
import { ArrayInput } from '../components/TextEditor/ArrayInput'
import { ObjectArrayInput } from '../components/TextEditor/ObjectArrayInput'
import type { SiteText } from '../types'

export function TextEditorPage() {
  const { data, isLoading, error } = useText('en')
  const publishMutation = usePublishText()

  const [draftText, setDraftText] = useState<SiteText | null>(null)
  const [etag, setEtag] = useState<string>('')

  // Load initial data
  useEffect(() => {
    if (data) {
      setDraftText(data.data)
      setEtag(data.etag)
    }
  }, [data])

  // Check if dirty
  const isDirty = JSON.stringify(draftText) !== JSON.stringify(data?.data || {})

  // Publish changes
  const handlePublish = () => {
    if (!draftText) return

    publishMutation.mutate(
      { data: draftText, etag, locale: 'en' },
      {
        onSuccess: (result) => {
          if (result.etag) {
            setEtag(result.etag)
          }
        },
      }
    )
  }

  // Reset to original
  const handleReset = () => {
    if (data) {
      setDraftText(data.data)
    }
  }

  // Helper to update nested values — uses functional updater so
  // multiple calls in the same event handler chain correctly.
  const updateField = (path: string[], value: unknown) => {
    setDraftText((prev) => {
      if (!prev) return prev

      const newDraft = { ...prev }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let current: any = newDraft // Need dynamic property access for nested traversal

      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = { ...current[path[i]] }
        current = current[path[i]]
      }

      current[path[path.length - 1]] = value
      return newDraft
    })
  }

  if (isLoading || !draftText) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="error">
        <p>Failed to load site text: {error instanceof Error ? error.message : 'Unknown error'}</p>
      </Alert>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Site Text Editor</h1>
            <p className="mt-1 text-sm text-gray-500">
              Edit all static text displayed on the public website. Changes are live immediately for
              new visitors. Returning visitors see updates within 6 hours.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleReset}
              variant="secondary"
              disabled={!isDirty || publishMutation.isPending}
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </Button>
            <Button onClick={handlePublish} disabled={!isDirty || publishMutation.isPending}>
              <Save className="h-4 w-4" />
              {publishMutation.isPending ? 'Publishing...' : 'Publish Changes'}
            </Button>
          </div>
        </div>

        {/* Status Messages */}
        {publishMutation.isSuccess && (
          <Alert variant="success">
            <p>Site text published successfully! New visitors will see changes immediately.</p>
            <details className="mt-2 text-sm">
              <summary className="cursor-pointer font-semibold hover:underline">
                Not seeing your changes?
              </summary>
              <p className="mt-1">
                The public site caches text for up to 6 hours. To see changes right away:
              </p>
              <ul className="list-disc ml-5 mt-1 space-y-0.5">
                <li>
                  <strong>Easiest:</strong> Open the site in a private/incognito window
                </li>
                <li>
                  <strong>Desktop:</strong> Open DevTools ({' '}
                  <kbd className="px-1 py-0.5 bg-green-100 rounded text-xs">F12</kbd> ) →
                  Application → Storage → Clear site data
                </li>
              </ul>
            </details>
          </Alert>
        )}

        {publishMutation.error && (
          <Alert variant="error">
            <p className="font-semibold">Publish Failed:</p>
            <p className="text-sm mt-1">
              {publishMutation.error instanceof Error
                ? publishMutation.error.message
                : 'Failed to publish changes'}
            </p>
          </Alert>
        )}

        {/* Warning about concurrent edits */}
        {isDirty && (
          <Alert variant="info">
            <p className="font-semibold">Unsaved Changes</p>
            <p className="text-sm mt-1">
              You have unpublished changes. If someone else publishes while you're editing, you'll
              need to refresh the page and re-apply your changes.
            </p>
          </Alert>
        )}

        {/* Metadata */}
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Version:</span>{' '}
              <span className="text-gray-900">{draftText.version || 1}</span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Last Updated:</span>{' '}
              <span className="text-gray-900">
                {draftText.updated_at ? new Date(draftText.updated_at).toLocaleString() : 'Never'}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-700">Updated By:</span>{' '}
              <span className="text-gray-900">{draftText.updated_by || 'System'}</span>
            </div>
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-4">
          {/* Navigation */}
          <CollapsibleSection title="Navigation">
            <TextInput
              label="Home"
              value={draftText.navigation.home}
              onChange={(v) => updateField(['navigation', 'home'], v)}
            />
            <TextInput
              label="About"
              value={draftText.navigation.about}
              onChange={(v) => updateField(['navigation', 'about'], v)}
            />
            <TextInput
              label="Calendar"
              value={draftText.navigation.calendar}
              onChange={(v) => updateField(['navigation', 'calendar'], v)}
            />
            <TextInput
              label="Roster"
              value={draftText.navigation.roster}
              onChange={(v) => updateField(['navigation', 'roster'], v)}
            />
            <TextInput
              label="News"
              value={draftText.navigation.news}
              onChange={(v) => updateField(['navigation', 'news'], v)}
            />
            <TextInput
              label="Links"
              value={draftText.navigation.links}
              onChange={(v) => updateField(['navigation', 'links'], v)}
            />
            <TextInput
              label="Support"
              value={draftText.navigation.support}
              onChange={(v) => updateField(['navigation', 'support'], v)}
            />
            <TextInput
              label="Contact"
              value={draftText.navigation.contact}
              onChange={(v) => updateField(['navigation', 'contact'], v)}
            />
          </CollapsibleSection>

          {/* Header */}
          <CollapsibleSection title="Header">
            <TextInput
              label="School Name"
              value={draftText.header.school_name}
              onChange={(v) => updateField(['header', 'school_name'], v)}
            />
            <TextInput
              label="Tagline"
              value={draftText.header.tagline}
              onChange={(v) => updateField(['header', 'tagline'], v)}
            />
            <TextInput
              label="Logo Alt Text"
              value={draftText.header.logo_alt}
              onChange={(v) => updateField(['header', 'logo_alt'], v)}
            />
          </CollapsibleSection>

          {/* Footer */}
          <CollapsibleSection title="Footer">
            <TextInput
              label="Copyright"
              value={draftText.footer.copyright}
              onChange={(v) => updateField(['footer', 'copyright'], v)}
            />
            <TextInput
              label="Attribution"
              value={draftText.footer.attribution}
              onChange={(v) => updateField(['footer', 'attribution'], v)}
            />
          </CollapsibleSection>

          {/* Home Page */}
          <CollapsibleSection title="Home Page">
            <div className="space-y-4">
              <div className="font-medium text-gray-700">Hero Section</div>
              <TextInput
                label="Title Line 1"
                value={draftText.home.hero.title_line1}
                onChange={(v) => updateField(['home', 'hero', 'title_line1'], v)}
              />
              <TextInput
                label="Title Line 2"
                value={draftText.home.hero.title_line2}
                onChange={(v) => updateField(['home', 'hero', 'title_line2'], v)}
              />
              <TextInput
                label="Subtitle"
                value={draftText.home.hero.subtitle}
                onChange={(v) => updateField(['home', 'hero', 'subtitle'], v)}
              />

              <div className="font-medium text-gray-700 mt-6">Call to Action Buttons</div>
              <TextInput
                label="View Schedule Button"
                value={draftText.home.cta.schedule}
                onChange={(v) => updateField(['home', 'cta', 'schedule'], v)}
              />
              <TextInput
                label="Team Button"
                value={draftText.home.cta.team}
                onChange={(v) => updateField(['home', 'cta', 'team'], v)}
              />

              <div className="font-medium text-gray-700 mt-6">Welcome Section</div>
              <TextInput
                label="Title"
                value={draftText.home.welcome.title}
                onChange={(v) => updateField(['home', 'welcome', 'title'], v)}
                multiline
                rows={2}
              />
              <TextInput
                label="Subtitle"
                value={draftText.home.welcome.subtitle}
                onChange={(v) => updateField(['home', 'welcome', 'subtitle'], v)}
                multiline
                rows={2}
              />

              <div className="font-medium text-gray-700 mt-6">Feature Cards</div>
              <div className="pl-4 space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-600">Spirit Wear</div>
                  <TextInput
                    label="Icon"
                    value={draftText.home.features.spirit_wear.icon}
                    onChange={(v) => updateField(['home', 'features', 'spirit_wear', 'icon'], v)}
                  />
                  <TextInput
                    label="Title"
                    value={draftText.home.features.spirit_wear.title}
                    onChange={(v) => updateField(['home', 'features', 'spirit_wear', 'title'], v)}
                  />
                  <TextInput
                    label="Description"
                    value={draftText.home.features.spirit_wear.description}
                    onChange={(v) =>
                      updateField(['home', 'features', 'spirit_wear', 'description'], v)
                    }
                    multiline
                  />
                  <TextInput
                    label="Link Text"
                    value={draftText.home.features.spirit_wear.link_text}
                    onChange={(v) =>
                      updateField(['home', 'features', 'spirit_wear', 'link_text'], v)
                    }
                  />
                  <LinkInput
                    linkHref={draftText.home.features.spirit_wear.link_href}
                    linkBadge={draftText.home.features.spirit_wear.link_badge}
                    onChangeHref={(v) =>
                      updateField(['home', 'features', 'spirit_wear', 'link_href'], v)
                    }
                    onChangeBadge={(v) =>
                      updateField(['home', 'features', 'spirit_wear', 'link_badge'], v)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-600">Media</div>
                  <TextInput
                    label="Icon"
                    value={draftText.home.features.media.icon}
                    onChange={(v) => updateField(['home', 'features', 'media', 'icon'], v)}
                  />
                  <TextInput
                    label="Title"
                    value={draftText.home.features.media.title}
                    onChange={(v) => updateField(['home', 'features', 'media', 'title'], v)}
                  />
                  <TextInput
                    label="Description"
                    value={draftText.home.features.media.description}
                    onChange={(v) => updateField(['home', 'features', 'media', 'description'], v)}
                    multiline
                  />
                  <TextInput
                    label="Link Text"
                    value={draftText.home.features.media.link_text}
                    onChange={(v) => updateField(['home', 'features', 'media', 'link_text'], v)}
                  />
                  <LinkInput
                    linkHref={draftText.home.features.media.link_href}
                    linkBadge={draftText.home.features.media.link_badge}
                    onChangeHref={(v) => updateField(['home', 'features', 'media', 'link_href'], v)}
                    onChangeBadge={(v) =>
                      updateField(['home', 'features', 'media', 'link_badge'], v)
                    }
                  />
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-gray-600">Fundraise</div>
                  <TextInput
                    label="Icon"
                    value={draftText.home.features.fundraise.icon}
                    onChange={(v) => updateField(['home', 'features', 'fundraise', 'icon'], v)}
                  />
                  <TextInput
                    label="Title"
                    value={draftText.home.features.fundraise.title}
                    onChange={(v) => updateField(['home', 'features', 'fundraise', 'title'], v)}
                  />
                  <TextInput
                    label="Description"
                    value={draftText.home.features.fundraise.description}
                    onChange={(v) =>
                      updateField(['home', 'features', 'fundraise', 'description'], v)
                    }
                    multiline
                  />
                  <TextInput
                    label="Link Text"
                    value={draftText.home.features.fundraise.link_text}
                    onChange={(v) => updateField(['home', 'features', 'fundraise', 'link_text'], v)}
                  />
                  <LinkInput
                    linkHref={draftText.home.features.fundraise.link_href}
                    linkBadge={draftText.home.features.fundraise.link_badge}
                    onChangeHref={(v) =>
                      updateField(['home', 'features', 'fundraise', 'link_href'], v)
                    }
                    onChangeBadge={(v) =>
                      updateField(['home', 'features', 'fundraise', 'link_badge'], v)
                    }
                  />
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* About Page */}
          <CollapsibleSection title="About Page">
            <TextInput
              label="Page Title"
              value={draftText.about.title}
              onChange={(v) => updateField(['about', 'title'], v)}
            />
            <TextInput
              label="Page Subtitle"
              value={draftText.about.subtitle}
              onChange={(v) => updateField(['about', 'subtitle'], v)}
              multiline
            />

            <div className="font-medium text-gray-700 mt-4">Vision</div>
            <TextInput
              label="Heading"
              value={draftText.about.vision.heading}
              onChange={(v) => updateField(['about', 'vision', 'heading'], v)}
            />
            <TextInput
              label="Text"
              value={draftText.about.vision.text}
              onChange={(v) => updateField(['about', 'vision', 'text'], v)}
              multiline
              rows={4}
            />

            <div className="font-medium text-gray-700 mt-4">Mission</div>
            <TextInput
              label="Heading"
              value={draftText.about.mission.heading}
              onChange={(v) => updateField(['about', 'mission', 'heading'], v)}
            />
            <ArrayInput
              label="Mission Points"
              values={draftText.about.mission.points}
              onChange={(v) => updateField(['about', 'mission', 'points'], v)}
              placeholder="Enter a mission point..."
            />

            <div className="font-medium text-gray-700 mt-4">Boosters</div>
            <TextInput
              label="Heading"
              value={draftText.about.boosters.heading}
              onChange={(v) => updateField(['about', 'boosters', 'heading'], v)}
            />
            <ObjectArrayInput
              label="Impact Items"
              values={draftText.about.boosters.impact}
              onChange={(v) => updateField(['about', 'boosters', 'impact'], v)}
              fields={[
                { key: 'icon', label: 'Icon' },
                { key: 'title', label: 'Title' },
                { key: 'description', label: 'Description', multiline: true, rows: 2 },
              ]}
              emptyTemplate={{ icon: '', title: '', description: '' }}
            />
            <TextInput
              label="Callout Text"
              value={draftText.about.boosters.callout}
              onChange={(v) => updateField(['about', 'boosters', 'callout'], v)}
              multiline
            />

            <div className="font-medium text-gray-700 mt-4">Call to Action</div>
            <TextInput
              label="Question"
              value={draftText.about.cta.question}
              onChange={(v) => updateField(['about', 'cta', 'question'], v)}
            />
            <TextInput
              label="Support Button"
              value={draftText.about.cta.support}
              onChange={(v) => updateField(['about', 'cta', 'support'], v)}
            />
            <TextInput
              label="Get Involved Button"
              value={draftText.about.cta.involve}
              onChange={(v) => updateField(['about', 'cta', 'involve'], v)}
            />
          </CollapsibleSection>

          {/* Donate Page */}
          <CollapsibleSection title="Donate Page">
            <TextInput
              label="Page Title"
              value={draftText.donate.title}
              onChange={(v) => updateField(['donate', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.donate.subtitle}
              onChange={(v) => updateField(['donate', 'subtitle'], v)}
              multiline
              rows={2}
            />
            <TextInput
              label="Impact Heading"
              value={draftText.donate.impact_heading}
              onChange={(v) => updateField(['donate', 'impact_heading'], v)}
            />
            <ObjectArrayInput
              label="Impact Items"
              values={draftText.donate.impact}
              onChange={(v) => updateField(['donate', 'impact'], v)}
              fields={[
                { key: 'icon', label: 'Icon' },
                { key: 'title', label: 'Title' },
                { key: 'description', label: 'Description', multiline: true, rows: 2 },
              ]}
              emptyTemplate={{ icon: '', title: '', description: '' }}
            />

            <div className="font-medium text-gray-700 mt-4">Donation Section</div>
            <TextInput
              label="Heading"
              value={draftText.donate.donation.heading}
              onChange={(v) => updateField(['donate', 'donation', 'heading'], v)}
            />
            <TextInput
              label="Description"
              value={draftText.donate.donation.description}
              onChange={(v) => updateField(['donate', 'donation', 'description'], v)}
              multiline
            />

            <div className="font-medium text-gray-700 mt-4">Other Ways to Support</div>
            <TextInput
              label="Heading"
              value={draftText.donate.other_ways.heading}
              onChange={(v) => updateField(['donate', 'other_ways', 'heading'], v)}
            />
            {/* Spirit Wear */}
            <div className="pl-4 space-y-2">
              <div className="text-sm font-medium text-gray-600">Spirit Wear</div>
              <TextInput
                label="Icon"
                value={draftText.donate.other_ways.spirit_wear.icon}
                onChange={(v) => updateField(['donate', 'other_ways', 'spirit_wear', 'icon'], v)}
              />
              <TextInput
                label="Title"
                value={draftText.donate.other_ways.spirit_wear.title}
                onChange={(v) => updateField(['donate', 'other_ways', 'spirit_wear', 'title'], v)}
              />
              <TextInput
                label="Description"
                value={draftText.donate.other_ways.spirit_wear.description}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'spirit_wear', 'description'], v)
                }
                multiline
              />
              <TextInput
                label="Link Text"
                value={draftText.donate.other_ways.spirit_wear.link_text}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'spirit_wear', 'link_text'], v)
                }
              />
              <LinkInput
                linkHref={draftText.donate.other_ways.spirit_wear.link_href}
                linkBadge={draftText.donate.other_ways.spirit_wear.link_badge}
                onChangeHref={(v) =>
                  updateField(['donate', 'other_ways', 'spirit_wear', 'link_href'], v)
                }
                onChangeBadge={(v) =>
                  updateField(['donate', 'other_ways', 'spirit_wear', 'link_badge'], v)
                }
              />
            </div>

            {/* Volunteer */}
            <div className="pl-4 space-y-2">
              <div className="text-sm font-medium text-gray-600">Volunteer</div>
              <TextInput
                label="Icon"
                value={draftText.donate.other_ways.volunteer.icon}
                onChange={(v) => updateField(['donate', 'other_ways', 'volunteer', 'icon'], v)}
              />
              <TextInput
                label="Title"
                value={draftText.donate.other_ways.volunteer.title}
                onChange={(v) => updateField(['donate', 'other_ways', 'volunteer', 'title'], v)}
              />
              <TextInput
                label="Description"
                value={draftText.donate.other_ways.volunteer.description}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'volunteer', 'description'], v)
                }
                multiline
              />
              <TextInput
                label="Link Text"
                value={draftText.donate.other_ways.volunteer.link_text}
                onChange={(v) => updateField(['donate', 'other_ways', 'volunteer', 'link_text'], v)}
              />
              <LinkInput
                linkHref={draftText.donate.other_ways.volunteer.link_href}
                linkBadge={draftText.donate.other_ways.volunteer.link_badge}
                onChangeHref={(v) =>
                  updateField(['donate', 'other_ways', 'volunteer', 'link_href'], v)
                }
                onChangeBadge={(v) =>
                  updateField(['donate', 'other_ways', 'volunteer', 'link_badge'], v)
                }
              />
            </div>

            {/* Spread Word */}
            <div className="pl-4 space-y-2">
              <div className="text-sm font-medium text-gray-600">Spread the Word</div>
              <TextInput
                label="Icon"
                value={draftText.donate.other_ways.spread_word.icon}
                onChange={(v) => updateField(['donate', 'other_ways', 'spread_word', 'icon'], v)}
              />
              <TextInput
                label="Title"
                value={draftText.donate.other_ways.spread_word.title}
                onChange={(v) => updateField(['donate', 'other_ways', 'spread_word', 'title'], v)}
              />
              <TextInput
                label="Description"
                value={draftText.donate.other_ways.spread_word.description}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'spread_word', 'description'], v)
                }
                multiline
              />
              <TextInput
                label="Link Text"
                value={draftText.donate.other_ways.spread_word.link_text || ''}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'spread_word', 'link_text'], v)
                }
              />
              <LinkInput
                linkHref={draftText.donate.other_ways.spread_word.link_href}
                linkBadge={draftText.donate.other_ways.spread_word.link_badge}
                onChangeHref={(v) =>
                  updateField(['donate', 'other_ways', 'spread_word', 'link_href'], v)
                }
                onChangeBadge={(v) =>
                  updateField(['donate', 'other_ways', 'spread_word', 'link_badge'], v)
                }
              />
            </div>

            {/* Attend Events */}
            <div className="pl-4 space-y-2">
              <div className="text-sm font-medium text-gray-600">Attend Events</div>
              <TextInput
                label="Icon"
                value={draftText.donate.other_ways.attend_events.icon}
                onChange={(v) => updateField(['donate', 'other_ways', 'attend_events', 'icon'], v)}
              />
              <TextInput
                label="Title"
                value={draftText.donate.other_ways.attend_events.title}
                onChange={(v) => updateField(['donate', 'other_ways', 'attend_events', 'title'], v)}
              />
              <TextInput
                label="Description"
                value={draftText.donate.other_ways.attend_events.description}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'attend_events', 'description'], v)
                }
                multiline
              />
              <TextInput
                label="Link Text"
                value={draftText.donate.other_ways.attend_events.link_text}
                onChange={(v) =>
                  updateField(['donate', 'other_ways', 'attend_events', 'link_text'], v)
                }
              />
              <LinkInput
                linkHref={draftText.donate.other_ways.attend_events.link_href}
                linkBadge={draftText.donate.other_ways.attend_events.link_badge}
                onChangeHref={(v) =>
                  updateField(['donate', 'other_ways', 'attend_events', 'link_href'], v)
                }
                onChangeBadge={(v) =>
                  updateField(['donate', 'other_ways', 'attend_events', 'link_badge'], v)
                }
              />
            </div>

            <div className="font-medium text-gray-700 mt-4">Thank You Section</div>
            <TextInput
              label="Heading"
              value={draftText.donate.thank_you.heading}
              onChange={(v) => updateField(['donate', 'thank_you', 'heading'], v)}
            />
            <TextInput
              label="Message"
              value={draftText.donate.thank_you.message}
              onChange={(v) => updateField(['donate', 'thank_you', 'message'], v)}
              multiline
              rows={3}
            />
          </CollapsibleSection>

          {/* Contact Page */}
          <CollapsibleSection title="Contact Page">
            <TextInput
              label="Page Title"
              value={draftText.contact.title}
              onChange={(v) => updateField(['contact', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.contact.subtitle}
              onChange={(v) => updateField(['contact', 'subtitle'], v)}
            />

            <div className="font-medium text-gray-700 mt-4">Form Labels</div>
            <TextInput
              label="Name Field"
              value={draftText.contact.form.name}
              onChange={(v) => updateField(['contact', 'form', 'name'], v)}
            />
            <TextInput
              label="Name Field (Required)"
              value={draftText.contact.form.name_required}
              onChange={(v) => updateField(['contact', 'form', 'name_required'], v)}
            />
            <TextInput
              label="Email Field"
              value={draftText.contact.form.email}
              onChange={(v) => updateField(['contact', 'form', 'email'], v)}
            />
            <TextInput
              label="Email Field (Required)"
              value={draftText.contact.form.email_required}
              onChange={(v) => updateField(['contact', 'form', 'email_required'], v)}
            />
            <TextInput
              label="Phone Field"
              value={draftText.contact.form.phone}
              onChange={(v) => updateField(['contact', 'form', 'phone'], v)}
            />
            <TextInput
              label="Message Field"
              value={draftText.contact.form.message}
              onChange={(v) => updateField(['contact', 'form', 'message'], v)}
            />
            <TextInput
              label="Message Field (Required)"
              value={draftText.contact.form.message_required}
              onChange={(v) => updateField(['contact', 'form', 'message_required'], v)}
            />
            <TextInput
              label="Minimum Characters Hint"
              value={draftText.contact.form.min_chars}
              onChange={(v) => updateField(['contact', 'form', 'min_chars'], v)}
            />
            <TextInput
              label="Submit Button"
              value={draftText.contact.form.submit}
              onChange={(v) => updateField(['contact', 'form', 'submit'], v)}
            />

            <div className="font-medium text-gray-700 mt-4">Messages</div>
            <TextInput
              label="CAPTCHA Required"
              value={draftText.contact.messages.captcha_required}
              onChange={(v) => updateField(['contact', 'messages', 'captcha_required'], v)}
            />
            <TextInput
              label="Success Message"
              value={draftText.contact.messages.success}
              onChange={(v) => updateField(['contact', 'messages', 'success'], v)}
            />
            <TextInput
              label="Validation Error"
              value={draftText.contact.messages.validation_error}
              onChange={(v) => updateField(['contact', 'messages', 'validation_error'], v)}
            />
            <TextInput
              label="Send Error"
              value={draftText.contact.messages.send_error}
              onChange={(v) => updateField(['contact', 'messages', 'send_error'], v)}
            />
            <TextInput
              label="API Error"
              value={draftText.contact.messages.api_error}
              onChange={(v) => updateField(['contact', 'messages', 'api_error'], v)}
            />
          </CollapsibleSection>

          {/* Calendar, Roster, News, Links Pages - Similar structure */}
          <CollapsibleSection title="Calendar Page">
            <TextInput
              label="Page Title"
              value={draftText.calendar.title}
              onChange={(v) => updateField(['calendar', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.calendar.subtitle}
              onChange={(v) => updateField(['calendar', 'subtitle'], v)}
            />
            <div className="font-medium text-gray-700 mt-4">Messages</div>
            <TextInput
              label="Loading"
              value={draftText.calendar.messages.loading}
              onChange={(v) => updateField(['calendar', 'messages', 'loading'], v)}
            />
            <TextInput
              label="Empty State"
              value={draftText.calendar.messages.empty}
              onChange={(v) => updateField(['calendar', 'messages', 'empty'], v)}
            />
            <TextInput
              label="Error"
              value={draftText.calendar.messages.error}
              onChange={(v) => updateField(['calendar', 'messages', 'error'], v)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Roster Page">
            <TextInput
              label="Page Title"
              value={draftText.roster.title}
              onChange={(v) => updateField(['roster', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.roster.subtitle}
              onChange={(v) => updateField(['roster', 'subtitle'], v)}
            />
            <TextInput
              label="Grade Label"
              value={draftText.roster.grade_label}
              onChange={(v) => updateField(['roster', 'grade_label'], v)}
            />
            <div className="font-medium text-gray-700 mt-4">Messages</div>
            <TextInput
              label="Loading"
              value={draftText.roster.messages.loading}
              onChange={(v) => updateField(['roster', 'messages', 'loading'], v)}
            />
            <TextInput
              label="Empty State"
              value={draftText.roster.messages.empty}
              onChange={(v) => updateField(['roster', 'messages', 'empty'], v)}
            />
            <TextInput
              label="Error"
              value={draftText.roster.messages.error}
              onChange={(v) => updateField(['roster', 'messages', 'error'], v)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="News Page">
            <TextInput
              label="Page Title"
              value={draftText.news.title}
              onChange={(v) => updateField(['news', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.news.subtitle}
              onChange={(v) => updateField(['news', 'subtitle'], v)}
            />
            <div className="font-medium text-gray-700 mt-4">Messages</div>
            <TextInput
              label="Loading"
              value={draftText.news.messages.loading}
              onChange={(v) => updateField(['news', 'messages', 'loading'], v)}
            />
            <TextInput
              label="Empty State"
              value={draftText.news.messages.empty}
              onChange={(v) => updateField(['news', 'messages', 'empty'], v)}
            />
            <TextInput
              label="Error"
              value={draftText.news.messages.error}
              onChange={(v) => updateField(['news', 'messages', 'error'], v)}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Links Page">
            <TextInput
              label="Page Title"
              value={draftText.links.title}
              onChange={(v) => updateField(['links', 'title'], v)}
            />
            <TextInput
              label="Subtitle"
              value={draftText.links.subtitle}
              onChange={(v) => updateField(['links', 'subtitle'], v)}
            />
            <TextInput
              label="Last Updated Text"
              value={draftText.links.updated}
              onChange={(v) => updateField(['links', 'updated'], v)}
            />
            <div className="font-medium text-gray-700 mt-4">Badge Labels</div>
            <TextInput
              label="Official"
              value={draftText.links.badges.official}
              onChange={(v) => updateField(['links', 'badges', 'official'], v)}
            />
            <TextInput
              label="Video"
              value={draftText.links.badges.video}
              onChange={(v) => updateField(['links', 'badges', 'video'], v)}
            />
            <TextInput
              label="PDF"
              value={draftText.links.badges.pdf}
              onChange={(v) => updateField(['links', 'badges', 'pdf'], v)}
            />
            <TextInput
              label="Internal"
              value={draftText.links.badges.internal}
              onChange={(v) => updateField(['links', 'badges', 'internal'], v)}
            />
            <TextInput
              label="Merch"
              value={draftText.links.badges.merch}
              onChange={(v) => updateField(['links', 'badges', 'merch'], v)}
            />
            <TextInput
              label="Instagram"
              value={draftText.links.badges.instagram}
              onChange={(v) => updateField(['links', 'badges', 'instagram'], v)}
            />
            <TextInput
              label="Photos"
              value={draftText.links.badges.photos}
              onChange={(v) => updateField(['links', 'badges', 'photos'], v)}
            />
            <div className="font-medium text-gray-700 mt-4">Messages</div>
            <TextInput
              label="Loading"
              value={draftText.links.messages.loading}
              onChange={(v) => updateField(['links', 'messages', 'loading'], v)}
            />
            <TextInput
              label="Empty State"
              value={draftText.links.messages.empty}
              onChange={(v) => updateField(['links', 'messages', 'empty'], v)}
            />
            <TextInput
              label="Error"
              value={draftText.links.messages.error}
              onChange={(v) => updateField(['links', 'messages', 'error'], v)}
            />
          </CollapsibleSection>

          {/* Common */}
          <CollapsibleSection title="Common UI Text">
            <TextInput
              label="Loading"
              value={draftText.common.loading}
              onChange={(v) => updateField(['common', 'loading'], v)}
            />
            <TextInput
              label="Error"
              value={draftText.common.error}
              onChange={(v) => updateField(['common', 'error'], v)}
            />
            <TextInput
              label="Save"
              value={draftText.common.save}
              onChange={(v) => updateField(['common', 'save'], v)}
            />
            <TextInput
              label="Cancel"
              value={draftText.common.cancel}
              onChange={(v) => updateField(['common', 'cancel'], v)}
            />
            <TextInput
              label="Close"
              value={draftText.common.close}
              onChange={(v) => updateField(['common', 'close'], v)}
            />
            <TextInput
              label="Delete"
              value={draftText.common.delete}
              onChange={(v) => updateField(['common', 'delete'], v)}
            />
            <TextInput
              label="Edit"
              value={draftText.common.edit}
              onChange={(v) => updateField(['common', 'edit'], v)}
            />
          </CollapsibleSection>
        </div>
      </div>
    </div>
  )
}
